import dayjs from "dayjs";
import { api } from "@service/api";
import { useEffect, useState } from "react";

import { Card } from "@components/cards/card";
import { Pagination } from "@components/pagination";
import { LoadingSkeleton } from "@components/skeleton-loading";
import { FaBroom, FaExpand, FaMinus } from "react-icons/fa6";
import { ZuToastifyStore } from "@hooks/stores/zustand-toastify-store";
import {
  ClientTypeTypes,
  IClientPublicListDTO,
} from "@hooks/dtos/IClientDTO";
import {
  clientTypes,
  getClientStatus,
  getClientType,
} from "@hooks/functions/Client/status";
import { abbreviateName, formatName } from "@hooks/functions/formatName";

interface IClientListParams {
  page: number;
  startAt: string;
  endAt: string;
}

export function ClientList() {
  const {
    actions: { pushToastify },
  } = ZuToastifyStore();

  const [show, setShow] = useState<{ filter: boolean }>({
    filter: false,
  });

  const [page, setPage] = useState<number>(1);
  const [take, setTake] = useState<number>(10);
  const [order, setOrder] = useState("DESC");
  const [totalPages, setTotalPages] = useState<number>(1);

  const [searchClient, setSearchClient] = useState<string | undefined>();
  const [type, setType] = useState<ClientTypeTypes | undefined>();
  const [startAt, setStartDate] = useState("");
  const [endAt, setEndDate] = useState("");

  const [isLoading, setIsLoading] = useState({
    card: false,
    history: true,
  });

  const [clients, setClients] = useState<IClientPublicListDTO[]>();

  async function getClientList({ page, startAt, endAt }: IClientListParams) {
    try {
      const params = `?${new URLSearchParams(
        Object.entries({
          page: (page || 1).toString(),
          take: take.toString(),
          order,
          ...(searchClient && { search: searchClient }),
          ...(type && { type }),
          ...(startAt && { startAt }),
          ...(endAt && { endAt }),
        }),
      ).toString()}`;
      const result = await api.get(`/misc/client/search${params}`);

      setClients(result.data.clients);
      setTotalPages(result.data.totalPages);
      setIsLoading((prev) => ({ ...prev, history: false }));
    } catch (err: any) {
      setIsLoading((prev) => ({ ...prev, history: false }));
      pushToastify({
        message: err?.response?.data.message ?? "Ocorreu um erro!",
        type: "error",
        theme: "dark",
      });
    }
  }

  async function cleanFilters() {
    setType(undefined);
    setSearchClient(undefined);
    setStartDate("");
    setEndDate("");
    handleSubmit("", "");
  }

  function handleSubmit(_startDate: string, _endDate: string) {
    setIsLoading({ card: true, history: true });
    setPage(1);
    getClientList({
      page: 1,
      startAt: _startDate,
      endAt: _endDate,
    });
  }

  useEffect(() => {
    getClientList({
      page,
      startAt,
      endAt,
    });
  }, [page]);

  useEffect(() => {
    getClientList({
      page,
      startAt,
      endAt,
    });
  }, []);

  return (
    <>
      <div className="row">
        <div className="col-md-12">
          <Card>
            <div
              className="card-header"
              style={{ cursor: "pointer" }}
              onClick={() => {
                setShow((prev) => ({
                  ...prev,
                  filter: !show.filter,
                }));
              }}
            >
              <div className="d-flex justify-content-between">
                <div>
                  <div className="card-title">Filtrar Registros</div>
                  <div className="card-category">
                    Utilize o filtro para trazer um período específico.
                  </div>
                </div>
                <div>
                  {show.filter ? (
                    <FaMinus
                      className="fs-5"
                      title="Clique para ocultar as informações."
                    />
                  ) : (
                    <FaExpand
                      className="fs-5"
                      title="Clique para visualizar as informações."
                    />
                  )}
                </div>
              </div>
            </div>
            <div className={`card-body ${show.filter ? `` : `d-none`}`}>
              <div className="row g-3 align-items-end">
                <div className="col-md-12 col-lg-5">
                  <div className="form-group mb-0">
                    <label className="form-label small fw-semibold mb-1" htmlFor="searchClient">
                      Nome ou @arroba
                    </label>
                    <input
                      id="searchClient"
                      className="form-control"
                      type="text"
                      value={searchClient || ""}
                      onChange={(e) => {
                        setSearchClient(e.target.value);
                      }}
                      onKeyUp={(e) =>
                        e.key === "Enter" && handleSubmit(startAt, endAt)
                      }
                    />
                  </div>
                </div>
                <div className="col-md-4 col-lg-3">
                  <div className="form-group mb-0">
                    <label className="form-label small fw-semibold mb-1" htmlFor="clientType">
                      Tipo
                    </label>
                    <select
                      id="clientType"
                      className="form-select"
                      aria-label="Tipo de Cliente"
                      value={type || ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        setType(value === "" ? undefined : (value as ClientTypeTypes));
                      }}
                    >
                      <option value="">Todos</option>
                      {clientTypes.map((clientType) => {
                        return (
                          <option key={clientType.type} value={clientType.type}>
                            {clientType.text}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                </div>
                <div className="col-md-4 col-lg-2">
                  <div className="form-group mb-0">
                    <label className="form-label small fw-semibold mb-1" htmlFor="startAt">
                      Data Inicial
                    </label>
                    <input
                      id="startAt"
                      className="form-control"
                      type="date"
                      value={startAt}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </div>
                </div>
                <div className="col-md-4 col-lg-2">
                  <div className="form-group mb-0">
                    <label className="form-label small fw-semibold mb-1" htmlFor="endAt">
                      Data Final
                    </label>
                    <input
                      id="endAt"
                      className="form-control"
                      type="date"
                      value={endAt}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </div>
                </div>
              </div>
              <div className="border-top mt-3 pt-3">
                <div className="d-flex justify-content-end gap-2">
                    <button
                      className="btn btn-sm btn-success px-4"
                      onClick={() => handleSubmit(startAt, endAt)}
                    >
                      Filtrar
                    </button>
                    <button
                      className="btn btn-sm btn-warning text-white px-3"
                      onClick={cleanFilters}
                      title="Limpar todos os filtros."
                    >
                      <FaBroom />
                    </button>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>

      <div className="row">
        <div className="col-md-12">
          <Card>
            <div className="card-header">
              <div className="row">
                <div className="col-md-8">
                  <h4 className="card-title">Relação de Clientes</h4>
                  <p className="card-category">Clientes da Plataforma</p>
                </div>
                <div className="col-md-4">
                  <div className="row align-items-center">
                    <div className="col-md-4">
                      <div className="form-group">
                        <select
                          className="form-select"
                          aria-label="Quantidade de Registros por Página"
                          value={take}
                          onChange={(e) => setTake(Number(e.target.value))}
                        >
                          <option value="10">10</option>
                          <option value="25">25</option>
                          <option value="50">50</option>
                          <option value="75">75</option>
                          <option value="100">100</option>
                        </select>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="form-group">
                        <select
                          className="form-select"
                          aria-label="Ordenação da Tabela"
                          value={order}
                          onChange={(e) => setOrder(e.target.value)}
                        >
                          <option value="ASC">Crescente</option>
                          <option value="DESC">Decrescente</option>
                        </select>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="form-group">
                        <button
                          className="btn btn-sm btn-success form-control px-2"
                          onClick={() => handleSubmit(startAt, endAt)}
                        >
                          Aplicar
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                <div
                  id="basic-datatables_wrapper"
                  className="dataTables_wrapper container-fluid dt-bootstrap4"
                >
                  <div
                    id="basic-datatables_length"
                    className="dataTables_length"
                  >
                    <table className="table table-striped">
                      <thead>
                        <tr role="row">
                          <th style={{ minWidth: "2rem" }}>Identificação</th>
                          <th>Tipo</th>
                          <th>Status</th>
                          <th>Cadastro</th>
                        </tr>
                      </thead>
                      <tbody>
                        {isLoading.history && (
                          <tr>
                            <td colSpan={4} className="text-center">
                              <LoadingSkeleton />
                            </td>
                          </tr>
                        )}

                        {clients &&
                          clients.length > 0 &&
                          clients.map((client) => {
                            const status = getClientStatus(client.status);
                            const type = getClientType(client.type);
                            const clientName = client?.name
                              ? formatName(abbreviateName(client.name, 18))
                              : "~";
                            return (
                              <tr key={client?.id}>
                                <td>
                                  <div className="d-flex flex-column">
                                    <span title={formatName(client?.name || "")}>
                                      {clientName}
                                    </span>
                                    <small className="text-muted">
                                      @{client?.atSign || "~"}
                                    </small>
                                  </div>
                                </td>
                                <td>
                                  <span
                                    className={`badge bg-${type.color}`}
                                    title={type.text}
                                  >
                                    {type.title}
                                  </span>
                                </td>
                                <td>
                                  <span
                                    className={`badge bg-${status.color}`}
                                    title={status.text}
                                  >
                                    {status.title}
                                  </span>
                                </td>
                                <td>
                                  {dayjs(client?.createdAt).format(
                                    "DD/MM/YYYY [às] HH:mm:ss",
                                  )}
                                </td>
                              </tr>
                            );
                          })}

                        {isLoading.history ||
                          !clients ||
                          (clients?.length <= 0 && (
                            <tr>
                              <td colSpan={4} className="text-center">
                                Nenhum registro encontrado.
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              <Pagination
                page={page}
                setPage={setPage}
                totalPages={totalPages}
                maxPageShow={3}
              />
            </div>
          </Card>
        </div>
      </div>
    </>
  );
}
